import { Button, Input, Loader, ViewMode } from '@ytl/common-web';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../../../contexts/appContext';
import { useLanguage } from '../../../../contexts/languageContext';
import { AppRoutes } from '../../../../routes';
import { searchOnEnter } from '../../../utils/searchOnEnter';

export type AccountDataEditorType = 'email' | 'name' | 'phone';

export interface AccountDataEditorProps {
  label: string;
  value: string;
  type: AccountDataEditorType;

  viewMode: ViewMode;
  canModify: (type: AccountDataEditorType) => Promise<boolean>;
  canCancel: (type: AccountDataEditorType) => Promise<boolean>;
  showModify?: boolean;
  onSave: (type: AccountDataEditorType, value: string, value2?: string) => Promise<Array<string> | boolean>;
  onValueChange: (type: AccountDataEditorType) => void;
}

export const AccountDataEditor = ({
  label,
  value,
  type,
  viewMode,
  canCancel,
  showModify,
  onSave,
  onValueChange,
}: AccountDataEditorProps) => {
  const { translation } = useLanguage();
  const { isLoading, setLoading } = useApp();
  const navigate = useNavigate();
  const [editedValue, setEditedValue] = useState(value);
  const [editedValue2] = useState(value);
  const [errorList, setErrorList] = useState<Array<string>>([]);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Save changes
  const updateClicked = useCallback(async () => {
    try {
      setLoading(true);
      const res = await onSave(type, editedValue, editedValue2);
      if (res instanceof Array) {
        setErrorList(res);
      } else {
        setErrorList([]);
      }
    } finally {
      setLoading(false);
    }
  }, [editedValue, onSave, setLoading, type, editedValue2]);

  const cancelEdit = useCallback(async () => {
    const shouldContinue = await canCancel(type);
    if (shouldContinue) {
      setEditedValue(value);
      setErrorList([]);
    }
  }, [canCancel, value, type]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEditedValue(e.target.value);
      onValueChange(type);
    },
    [onValueChange, type],
  );

  return (
    <>
      <div className={'account-data__editor'}>
        <Input
          label={label}
          name={type}
          disabled={viewMode === ViewMode.View || isLoading}
          value={viewMode === ViewMode.View ? value : editedValue}
          onChange={onChange}
          type={type === 'email' ? 'email' : 'text'}
          inputMode={type === 'phone' ? 'tel' : type === 'email' ? 'email' : 'text'}
          errorList={errorList}
          onKeyUp={e => {
            setErrorList([]);
            searchOnEnter(() => updateClicked())(e);
          }}
          ref={firstInputRef}
          viewMode={viewMode}
          {...(type === 'email' && { clearable: true, onClear: () => setEditedValue('') })}
        />
      </div>

      <div className={'account-data__button-row'}>
        {viewMode === ViewMode.Edit && (
          <>
            <Button
              variant={'outlined'}
              size={'small'}
              onClick={cancelEdit}
              disabled={isLoading}
              aria-label={`${label} ${translation.accountSettings.accountDataCard.cancelButton}`}
            >
              {translation.accountSettings.accountDataCard.cancelButton}
            </Button>
            <Button
              variant={'contained'}
              size={'small'}
              onClick={updateClicked}
              disabled={isLoading}
              aria-label={`${label} ${translation.accountSettings.accountDataCard.saveButton}`}
            >
              <Loader isLoading={isLoading} />
              {translation.accountSettings.accountDataCard.saveButton}
            </Button>
          </>
        )}
        {viewMode === ViewMode.View && showModify !== false && (type === 'email' || type === 'phone') && (
          <Button
            variant={'text'}
            onClick={() => navigate(type === 'email' ? AppRoutes.emailChange : AppRoutes.phoneChange)}
            aria-label={`${label} ${translation.accountSettings.accountDataCard.modifyButton}`}
          >
            {translation.accountSettings.accountDataCard.modifyButton}
          </Button>
        )}
      </div>
    </>
  );
};
